/**
 * Folder utility functions and source type configuration.
 *
 * Centralizes source type metadata (icons, colors, labels) used by
 * FolderCard, folder dialogs, and folder list views.
 */

// -- Source Type Options --------------------------------------------

export const SOURCE_TYPES: { value: string; label: string }[] = [
  { value: "interview", label: "Interview" },
  { value: "focusGroup", label: "Focus Group" },
  { value: "survey", label: "Survey" },
  { value: "userStudy", label: "User Study" },
  { value: "meeting", label: "Meeting" },
  { value: "podcast", label: "Podcast" },
  { value: "webinar", label: "Webinar" },
  { value: "lecture", label: "Lecture" },
  { value: "content", label: "Content" },
  { value: "phoneCall", label: "Phone Call" },
  { value: "surveyData", label: "Survey Data" },
  { value: "other", label: "Other" },
];

// -- Source Type Visual Config --------------------------------------

interface SourceTypeConfig {
  icon: string;
  label: string;
  colorClass: string;
  bgClass: string;
  borderClass: string;
}

const SOURCE_TYPE_CONFIG_MAP: Record<string, SourceTypeConfig> = {
  interview: {
    icon: "message-square",
    label: "Interview",
    colorClass: "text-blue-500",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500",
  },
  focusGroup: {
    icon: "users",
    label: "Focus Group",
    colorClass: "text-violet-500",
    bgClass: "bg-violet-500/10",
    borderClass: "border-violet-500",
  },
  survey: {
    icon: "clipboard-list",
    label: "Survey",
    colorClass: "text-green-500",
    bgClass: "bg-green-500/10",
    borderClass: "border-green-500",
  },
  userStudy: {
    icon: "user-check",
    label: "User Study",
    colorClass: "text-amber-500",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500",
  },
  meeting: {
    icon: "calendar",
    label: "Meeting",
    colorClass: "text-cyan-500",
    bgClass: "bg-cyan-500/10",
    borderClass: "border-cyan-500",
  },
  podcast: {
    icon: "mic",
    label: "Podcast",
    colorClass: "text-rose-500",
    bgClass: "bg-rose-500/10",
    borderClass: "border-rose-500",
  },
  webinar: {
    icon: "video",
    label: "Webinar",
    colorClass: "text-indigo-500",
    bgClass: "bg-indigo-500/10",
    borderClass: "border-indigo-500",
  },
  lecture: {
    icon: "book-open",
    label: "Lecture",
    colorClass: "text-teal-500",
    bgClass: "bg-teal-500/10",
    borderClass: "border-teal-500",
  },
  content: {
    icon: "layout",
    label: "Content",
    colorClass: "text-orange-500",
    bgClass: "bg-orange-500/10",
    borderClass: "border-orange-500",
  },
  phoneCall: {
    icon: "phone",
    label: "Phone Call",
    colorClass: "text-lime-500",
    bgClass: "bg-lime-500/10",
    borderClass: "border-lime-500",
  },
  surveyData: {
    icon: "bar-chart-2",
    label: "Survey Data",
    colorClass: "text-pink-500",
    bgClass: "bg-pink-500/10",
    borderClass: "border-pink-500",
  },
};

const DEFAULT_CONFIG: SourceTypeConfig = {
  icon: "folder",
  label: "Other",
  colorClass: "text-muted-foreground",
  bgClass: "bg-muted",
  borderClass: "border-border",
};

/**
 * Returns visual config (icon name, Tailwind color/bg/border classes)
 * for a given source type. Falls back to a neutral "folder" style
 * when the source type is undefined or unrecognized.
 */
export function getSourceTypeConfig(sourceType?: string): SourceTypeConfig {
  if (!sourceType) return DEFAULT_CONFIG;
  return SOURCE_TYPE_CONFIG_MAP[sourceType] ?? DEFAULT_CONFIG;
}
